import { UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkFigure } from "uitk-react-images";
import { UitkIcon } from "uitk-react-icons";
import * as React from "react";
import { decorateEditorialHtml } from "components/flexComponents/Editorial/ContentUtilities";
import { uitkFigureAspectRatio } from "../editorials/EditorialWithLink";
import { LazyLoad } from "src/components/utility/LazyLoad/LazyLoad";
import { BlossomImage } from "src/components/shared/BlossomImage/BlossomImage";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { ModuleViewType } from "components/flexComponents/Editorial/factory/EditorialContentFactory";
import { useRenderableContext } from "./DefaultEditorial";
import { UitkLink } from "uitk-react-link";
import { RenderableContentCardProps } from "./RenderableContentCard";
import { UitkCard, UitkCardLink } from "uitk-react-cards";
import { SpacingSize } from "src/components/utility/FlexAttributesUtil";

/**
 * Editorial On Canvas component that sits the content directly
 * in the DOM (without a card).
 *
 * IMPORTANT: As of May 17th 2021, we agreed on adding a workaround
 * placing <UitkCard/> as its comp's parent element to support
 * rounded corner image using existing Uitk classes, and <UitkCardLink/>
 * to support the whole card clickable. All this to not adding more custom
 * CSS to blossom's layout. This is just TEMPORARY and it is not going to be
 * permanent, this was already discussed with EGDS and we are going to contribute
 * to UITK for round corners in images once it is in EGDS.
 * More info in: https://expedia.slack.com/archives/C88UW0PU7/p1621006460220000
 *
 *
 */

interface CollapsableContentProps extends Pick<RenderableContentCardProps, "toggleStatus" | "flipToggleStatus"> {}
interface ContentSectionProps extends Pick<RenderableContentCardProps, "displayEditorialClassNames"> {
  isExpandoEnabled?: boolean | string;
}
interface RenderableContentOnCanvasProps extends RenderableContentCardProps {}

export const RenderableContentOnCanvas = ({
  isExpandoEnabled,
  toggleStatus,
  flipToggleStatus,
  displayEditorialClassNames,
}: RenderableContentOnCanvasProps) => {
  const { context, key, media } = useRenderableContext();
  const mediaUrl = media?.clickUrl;
  const mediaCaption = media?.mediaCaption;

  return (
    <LazyLoad context={context}>
      <UitkCard className="noBackground cardWithLinkText">
        <UitkCardLink>
          <a href={mediaUrl}>{mediaCaption}</a>
        </UitkCardLink>
        <UitkLayoutGrid key={key}>
          {media?.mediaUrl && <MediaContent />}
          {isExpandoEnabled && <CollapsableContent toggleStatus={toggleStatus} flipToggleStatus={flipToggleStatus} />}
          <div
            data-testid="editorial-content"
            className={`editorialContent ${isExpandoEnabled && toggleStatus ? "is-visually-hidden" : ""}`}
          >
            <ContentSection
              isExpandoEnabled={isExpandoEnabled}
              displayEditorialClassNames={displayEditorialClassNames}
            />
          </div>
        </UitkLayoutGrid>
      </UitkCard>
    </LazyLoad>
  );
};

const CollapsableContent: React.FC<CollapsableContentProps> = ({ toggleStatus, flipToggleStatus }) => {
  const { title, subtitle } = useRenderableContext();

  return (
    <div tabIndex={0} role="button" aria-expanded={!toggleStatus} className="header" onClick={flipToggleStatus}>
      <UitkLayoutGrid columns={2}>
        <UitkLayoutGridItem colSpan={1}>
          <UitkHeading tag="h2" size={4}>
            {title}
          </UitkHeading>
        </UitkLayoutGridItem>
        <UitkLayoutGridItem colSpan={1}>
          <UitkLayoutFlex justifyContent="end">
            <UitkLayoutFlexItem>
              <div>
                <UitkSpacing margin={{ blockstart: "six" }}>
                  <UitkSubheading tag="h3">{subtitle}</UitkSubheading>
                </UitkSpacing>
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <UitkIcon name={`expand_${toggleStatus ? "more" : "less"}`} />
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    </div>
  );
};

const ContentSection: React.FC<ContentSectionProps> = ({ isExpandoEnabled, displayEditorialClassNames }) => {
  const { title, subtitle, text, media, view, trackRfrridClick, padding, id } = useRenderableContext();

  const mediaUrl = media?.clickUrl;
  const mediaCaption = media?.mediaCaption;
  const withPadding = padding !== SpacingSize.NONE;

  const showClickableMedia = mediaUrl && mediaCaption;

  const isSingleColumn = view === ModuleViewType.SingleColumnOnCanvas;

  const textClassNames = isSingleColumn ? "singleColumn" : "editorialTextColumn";

  return (
    <UitkSpacing padding={withPadding ? { block: "three" } : "unset"}>
      <UitkLayoutGridItem>
        <div className={displayEditorialClassNames} data-fm-content={id} id={id}>
          {title && !isExpandoEnabled && (
            <UitkHeading tag="h2" size={4}>
              {title}
            </UitkHeading>
          )}
          {subtitle && !isExpandoEnabled && <UitkSubheading tag="h4">{subtitle}</UitkSubheading>}
          {text && (
            <UitkSpacing padding={{ blockstart: "two" }}>
              <div
                onClick={trackRfrridClick && trackRfrridClick(useClickTracker())}
                className={`uitk-type-list-300 ${textClassNames}`}
                dangerouslySetInnerHTML={decorateEditorialHtml(text)}
              />
            </UitkSpacing>
          )}
          {showClickableMedia && (
            <UitkSpacing margin={{ blockstart: "four" }} padding="unset">
              <UitkLink>
                <a href={mediaUrl}>{mediaCaption}</a>
              </UitkLink>
            </UitkSpacing>
          )}
        </div>
      </UitkLayoutGridItem>
    </UitkSpacing>
  );
};

const MediaContent: React.FC = () => {
  const { media, imageAspectRatio = "16-9" } = useRenderableContext();

  return (
    <UitkLayoutGridItem>
      <div data-testid="image-media-content">
        <UitkFigure className="uitk-card-roundcorner-all" ratio={uitkFigureAspectRatio(imageAspectRatio)}>
          <BlossomImage noCrop={true} src={media?.mediaUrl || ""} alt={media?.mediaAlt || ""} />
        </UitkFigure>
      </div>
    </UitkLayoutGridItem>
  );
};
